import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import {
  QuestionMarkCircleIcon,
  EnvelopeIcon,
  BookOpenIcon,
  LinkIcon,
  LifebuoyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';

const FAQS = [
  {
    category: 'Getting Started',
    questions: [
      { q: 'How do I create an account?', a: 'Click "Register" in the top right corner. Fill in your name, email, and password — or sign in with Google for faster setup.' },
      { q: 'Is it free to join?', a: 'Yes. Browsing services and creating an account are completely free. You only pay when you book a service.' },
      { q: 'How do I find providers near me?', a: 'Use the search bar — enter your location or category, and we will show approved providers in your area.' },
    ],
  },
  {
    category: 'Bookings & Appointments',
    questions: [
      { q: 'How do I book an appointment?', a: 'Pick a provider, choose a service, select an available time slot, and confirm. You will see the booking under "My Bookings".' },
      { q: 'Can I cancel or reschedule?', a: 'Yes — open the booking from "My Bookings" and pick Cancel or Reschedule. Provider cancellation policies apply.' },
      { q: 'What if a provider declines my booking?', a: 'You will be notified instantly and any holds on your payment are released within 24 hours.' },
    ],
  },
  {
    category: 'Payments & Refunds',
    questions: [
      { q: 'What payment methods do you accept?', a: 'Mobile money (MTN, Airtel), Visa/Mastercard, and other regional methods shown at checkout.' },
      { q: 'When am I charged?', a: 'Payment is captured at booking confirmation. The provider receives the payout after the service is marked complete.' },
      { q: 'How do refunds work?', a: 'Refunds follow the provider\'s cancellation policy and are returned to your original payment method within 5–7 business days.' },
    ],
  },
  {
    category: 'Account & Security',
    questions: [
      { q: 'How do I reset my password?', a: 'Open Settings → Security → Change Password. Or use "Forgot password" from the login page.' },
      { q: 'How is my data protected?', a: 'All sensitive data is encrypted in transit and at rest. Read our Privacy Policy for full details.' },
      { q: 'How do I delete my account?', a: 'Settings → Account → Danger Zone → Delete Account. Account deletion is permanent.' },
    ],
  },
  {
    category: 'For Providers',
    questions: [
      { q: 'How do I become a provider?', a: 'Register, then complete your business profile. Our team verifies new providers within 24–48 hours.' },
      { q: 'What commission do you charge?', a: 'A small percentage per completed booking — see your provider agreement for the exact rate. No upfront fees.' },
    ],
  },
];

const GUIDES = [
  { title: 'Booking your first service', desc: 'A 3-step walkthrough from search to confirmation', to: '/search', icon: BookOpenIcon },
  { title: 'Managing your bookings', desc: 'Reschedule, cancel, or message your provider', to: '/bookings', icon: BookOpenIcon },
  { title: 'Updating your profile', desc: 'Change your name, photo, phone number', to: '/profile', icon: BookOpenIcon },
  { title: 'Privacy & notification settings', desc: 'Control what we send and what you share', to: '/settings', icon: BookOpenIcon },
];

const PROVIDER_GUIDES = [
  { title: 'Setting up your business profile', desc: 'Add details, photos, and verification docs', to: '/provider/profile', icon: BookOpenIcon },
  { title: 'Managing services & pricing', desc: 'Add, edit, or remove the services you offer', to: '/provider/services', icon: BookOpenIcon },
  { title: 'Setting your availability', desc: 'Configure working hours and time off', to: '/provider/availability', icon: BookOpenIcon },
  { title: 'Tracking earnings & analytics', desc: 'Understand your performance metrics', to: '/provider/analytics', icon: BookOpenIcon },
];

const Help = () => {
  const { user, isProvider } = useAuth();
  const [activeTab, setActiveTab] = useState('faq');
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [contactErrors, setContactErrors] = useState({});

  const filteredFaqs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS
      .map(cat => ({
        ...cat,
        questions: cat.questions.filter(item =>
          item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
        ),
      }))
      .filter(cat => cat.questions.length > 0);
  }, [search]);

  const handleContactSubmit = (e) => {
    e?.preventDefault?.();
    const nextErrors = {};
    if (!contactForm.name.trim()) nextErrors.name = 'Name is required';
    if (!contactForm.email.trim()) nextErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      nextErrors.email = 'Please enter a valid email address';
    }
    if (!contactForm.message.trim()) nextErrors.message = 'Message is required';
    setContactErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      const tickets = JSON.parse(localStorage.getItem('supportTickets') || '[]');
      const ticket = {
        id: `T-${Date.now().toString(36).toUpperCase()}`,
        ...contactForm,
        createdAt: new Date().toISOString(),
        status: 'open',
      };
      tickets.unshift(ticket);
      localStorage.setItem('supportTickets', JSON.stringify(tickets.slice(0, 50)));

      const mailtoBody = encodeURIComponent(
        `Name: ${contactForm.name}\nEmail: ${contactForm.email}\nCategory: ${contactForm.category}\n\n${contactForm.message}`
      );
      const mailtoSubject = encodeURIComponent(contactForm.subject || `Support request — ${contactForm.category}`);
      window.open(`mailto:support@helloparlour.com?subject=${mailtoSubject}&body=${mailtoBody}`, '_blank');

      toast.success(`Ticket ${ticket.id} created`);
      setContactForm(f => ({ ...f, subject: '', message: '' }));
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const updateContactField = (field, value) => {
    setContactForm(f => ({ ...f, [field]: value }));
    setContactErrors(e => ({ ...e, [field]: '' }));
  };

  const tabs = [
    { id: 'faq', label: 'FAQs', icon: QuestionMarkCircleIcon },
    { id: 'contact', label: 'Contact Support', icon: EnvelopeIcon },
    { id: 'guides', label: 'Guides', icon: BookOpenIcon },
    { id: 'resources', label: 'Resources', icon: LinkIcon },
  ];

  const guides = isProvider ? PROVIDER_GUIDES : GUIDES;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Help Center</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Find answers, browse guides, or reach out to our support team
          </p>
        </div>

        {/* Banner */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
        >
          <LifebuoyIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
            Average response time: under 4 hours · Mon–Sat, 8 AM – 8 PM
          </span>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {/* FAQ */}
          {activeTab === 'faq' && (
            <div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search FAQs..."
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--gray-5)',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--gray-12)',
                  fontSize: '14px',
                  outline: 'none',
                  marginBottom: '20px',
                }}
              />

              {filteredFaqs.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--gray-10)', fontSize: '14px', padding: '24px 0' }}>
                  No matches found. Try the Contact Support tab.
                </p>
              ) : (
                filteredFaqs.map((cat, ci) => (
                  <div key={cat.category} style={{ marginTop: ci > 0 ? '24px' : 0 }}>
                    <p
                      style={{
                        fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: 'var(--gray-10)',
                        margin: '0 0 10px', textAlign: 'left',
                      }}
                    >
                      {cat.category}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {cat.questions.map((item, qi) => {
                        const key = `${ci}-${qi}-${cat.category}`;
                        const open = openKey === key;
                        return (
                          <div
                            key={key}
                            className="rounded-lg"
                            style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                          >
                            <button
                              type="button"
                              onClick={() => setOpenKey(open ? null : key)}
                              className="w-full flex items-center justify-between"
                              style={{
                                padding: '12px 14px', border: 'none', background: 'transparent',
                                cursor: 'pointer', textAlign: 'left',
                              }}
                            >
                              <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', paddingRight: '12px' }}>
                                {item.q}
                              </span>
                              {open
                                ? <ChevronUpIcon style={{ width: '16px', height: '16px', color: 'var(--orange-9)', flexShrink: 0 }} />
                                : <ChevronDownIcon style={{ width: '16px', height: '16px', color: 'var(--gray-9)', flexShrink: 0 }} />
                              }
                            </button>
                            {open && (
                              <div style={{ padding: '0 14px 14px' }}>
                                <p style={{ fontSize: '13px', color: 'var(--gray-11)', margin: 0, lineHeight: 1.6, textAlign: 'left' }}>
                                  {item.a}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Contact */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              {/* Quick channels */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a
                  href="mailto:support@helloparlour.com"
                  className="rounded-lg p-4 flex items-center gap-3"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)', textDecoration: 'none' }}
                >
                  <EnvelopeIcon style={{ width: '22px', height: '22px', color: 'var(--orange-9)' }} />
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>Email</p>
                    <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>support@helloparlour.com</p>
                  </div>
                </a>
                <a
                  href="tel:+256123456789"
                  className="rounded-lg p-4 flex items-center gap-3"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)', textDecoration: 'none' }}
                >
                  <PhoneIcon style={{ width: '22px', height: '22px', color: 'var(--orange-9)' }} />
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>Phone</p>
                    <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>+256 123 456 789</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/256123456789"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg p-4 flex items-center gap-3"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)', textDecoration: 'none' }}
                >
                  <FaWhatsapp style={{ width: '22px', height: '22px', color: '#25D366' }} />
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>WhatsApp</p>
                    <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>Chat with us</p>
                  </div>
                </a>
              </div>

              {/* Form */}
              <form onSubmit={handleContactSubmit} noValidate className="space-y-4">
                <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                  Send us a message
                </p>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '-8px 0 0', textAlign: 'left' }}>
                  We will reply by email within 4 hours during business hours.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Your Name"
                    name="name"
                    value={contactForm.name}
                    onChange={(e) => updateContactField('name', e.target.value)}
                    placeholder="Full name"
                    error={contactErrors.name}
                    required
                  />
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={(e) => updateContactField('email', e.target.value)}
                    placeholder="you@example.com"
                    error={contactErrors.email}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                    Category
                  </label>
                  <select
                    value={contactForm.category}
                    onChange={(e) => setContactForm(f => ({ ...f, category: e.target.value }))}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                    }}
                  >
                    <option value="general">General Question</option>
                    <option value="booking">Booking Issue</option>
                    <option value="payment">Payment / Refund</option>
                    <option value="account">Account / Security</option>
                    <option value="provider">Provider Issue</option>
                    <option value="bug">Report a Bug</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <Input
                  label="Subject"
                  name="subject"
                  value={contactForm.subject}
                  onChange={(e) => updateContactField('subject', e.target.value)}
                  placeholder="Briefly describe your issue"
                />

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                    Message <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => updateContactField('message', e.target.value)}
                    placeholder="Tell us what's going on..."
                    rows={5}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: `1px solid ${contactErrors.message ? 'var(--red-9, #e5484d)' : 'var(--gray-5)'}`, backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', fontSize: '14px', outline: 'none', resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                  {contactErrors.message && (
                    <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '4px 0 0', textAlign: 'left' }}>
                      {contactErrors.message}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button type="submit" loading={submitting}>
                    Send Message
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Guides */}
          {activeTab === 'guides' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {guides.map(({ title, desc, to, icon: Icon }) => (
                <Link
                  key={title}
                  to={to}
                  className="rounded-lg p-4"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)', textDecoration: 'none' }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      backgroundColor: 'var(--orange-3)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon style={{ width: '18px', height: '18px', color: 'var(--orange-9)' }} />
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                      {title}
                    </p>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                    {desc}
                  </p>
                </Link>
              ))}
            </div>
          )}

          {/* Resources */}
          {activeTab === 'resources' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Privacy Policy', desc: 'How we collect, use, and protect your data', to: '/privacy' },
                { label: 'Terms of Service', desc: 'The rules and agreements you accept by using the platform', to: '/terms' },
                { label: 'Full FAQ Page', desc: 'Browse the complete list of frequently asked questions', to: '/faq' },
                { label: 'Find Services', desc: 'Browse providers and book appointments', to: '/search' },
              ].map(({ label, desc, to }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center justify-between rounded-lg p-4"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)', textDecoration: 'none' }}
                >
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{label}</p>
                    <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>{desc}</p>
                  </div>
                  <span style={{ fontSize: '13px', color: 'var(--orange-9)', fontWeight: 500 }}>Open →</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
